import { useState, useCallback, useEffect, useRef } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Sparkles,
  BookOpen,
  PenTool,
  Lightbulb,
  Wand2,
  Users,
  Layers,
  Send,
  Loader2,
  Copy,
  Check,
  FileText,
  Target,
  Zap,
  MessageSquare,
  ArrowRight,
  RefreshCw,
  ChevronRight,
  GraduationCap,
  Star,
  Hash,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import { aiGenerateText, aiGenerateExercise, aiChat, aiSuggestContent } from "../../lib/api";

/**
 * AIAssistantPanel - Assistant IA refactorisé pour l'éditeur
 * Phase E: Prompts contextuels, génération de structures, différenciation
 */

// School levels with detailed info
const NIVEAUX = [
  { id: "gs", label: "Grande Section", shortName: "GS", age: "5-6 ans", cycle: 1 },
  { id: "cp", label: "Cours Préparatoire", shortName: "CP", age: "6-7 ans", cycle: 2 },
  { id: "ce1", label: "CE1", shortName: "CE1", age: "7-8 ans", cycle: 2 },
  { id: "ce2", label: "CE2", shortName: "CE2", age: "8-9 ans", cycle: 2 },
  { id: "cm1", label: "CM1", shortName: "CM1", age: "9-10 ans", cycle: 3 },
  { id: "cm2", label: "CM2", shortName: "CM2", age: "10-11 ans", cycle: 3 },
];

// Pedagogical structure templates
const STRUCTURE_TEMPLATES = [
  {
    id: "fiche-lecture",
    name: "Fiche de lecture",
    icon: "📖",
    description: "Texte + Questions de compréhension",
    structure: [
      { type: "header", content: "Titre du texte" },
      { type: "text", content: "Zone de texte" },
      { type: "questions", count: 5 },
      { type: "vocabulary", count: 4 },
    ],
  },
  {
    id: "exercice-grammaire",
    name: "Exercice de grammaire",
    icon: "✏️",
    description: "Règle + Exemples + Exercices",
    structure: [
      { type: "rule", content: "Règle grammaticale" },
      { type: "examples", count: 3 },
      { type: "exercises", count: 5 },
    ],
  },
  {
    id: "dictee-preparee",
    name: "Dictée préparée",
    icon: "📝",
    description: "Mots difficiles + Texte + Exercices",
    structure: [
      { type: "vocabulary", count: 8 },
      { type: "text", content: "Texte de la dictée" },
      { type: "exercises", count: 3 },
    ],
  },
  {
    id: "carte-mentale",
    name: "Carte mentale",
    icon: "🗺️",
    description: "Thème central + Branches",
    structure: [
      { type: "central", content: "Thème central" },
      { type: "branches", count: 4 },
    ],
  },
  {
    id: "lecon-sciences",
    name: "Leçon de sciences",
    icon: "🔬",
    description: "Observation + Hypothèse + Expérience",
    structure: [
      { type: "question", content: "Question de départ" },
      { type: "hypothesis", count: 2 },
      { type: "experiment", content: "Description de l'expérience" },
      { type: "conclusion", content: "Ce que j'ai appris" },
    ],
  },
  {
    id: "probleme-math",
    name: "Problème de maths",
    icon: "🧮",
    description: "Énoncé + Données + Résolution",
    structure: [
      { type: "problem", content: "Énoncé du problème" },
      { type: "data", content: "Données" },
      { type: "steps", count: 3 },
      { type: "answer", content: "Réponse" },
    ],
  },
];

// Quick generation prompts by subject
const QUICK_PROMPTS = {
  lecture: [
    "Une histoire courte sur les saisons",
    "Un documentaire sur les animaux de la forêt",
    "Une poésie sur la rentrée des classes",
    "Un conte sur l'amitié",
  ],
  grammaire: [
    "Exercices sur le pluriel des noms",
    "Identifier le verbe dans une phrase",
    "Les déterminants (le, la, les, un, une)",
    "L'accord sujet-verbe",
  ],
  vocabulaire: [
    "Les mots de la famille de 'terre'",
    "Les contraires (antonymes)",
    "Les synonymes du mot 'petit'",
    "Le champ lexical de la mer",
  ],
  maths: [
    "Problèmes d'addition et soustraction",
    "Les tables de multiplication",
    "Reconnaître les figures géométriques",
    "Lire l'heure sur une horloge",
  ],
};

// Differentiation options
const DIFFERENTIATION_OPTIONS = [
  { id: "simplify", label: "Simplifier", icon: "🔽", description: "Réduire la complexité" },
  { id: "enrich", label: "Enrichir", icon: "🔼", description: "Ajouter des défis" },
  { id: "visual", label: "Supports visuels", icon: "🎨", description: "Ajouter des aides visuelles" },
  { id: "audio", label: "Version audio", icon: "🔊", description: "Consignes orales" },
  { id: "scaffold", label: "Étayage", icon: "🪜", description: "Ajouter des aides" },
];

export function AIAssistantPanel({
  onClose,
  onGenerateText,
  onGenerateStructure,
  onGenerateBlock,
  onImproveContent,
  currentElements = [],
  documentTitle = "",
}) {
  const [activeTab, setActiveTab] = useState("generate");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  
  // Generation state
  const [prompt, setPrompt] = useState("");
  const [niveau, setNiveau] = useState("cp");
  const [subject, setSubject] = useState("lecture");
  const [contentType, setContentType] = useState("text");
  
  // Structure state
  const [selectedStructure, setSelectedStructure] = useState(null);
  
  // Chat state
  const [chatMessages, setChatMessages] = useState([
    { role: "assistant", content: "Bonjour ! Je suis Navi, votre assistant pédagogique. Comment puis-je vous aider à créer du contenu pour vos élèves ?" }
  ]);
  const [chatInput, setChatInput] = useState("");

  // Context from current document
  const documentContext = {
    title: documentTitle,
    elementCount: currentElements.length,
    hasText: currentElements.some(el => el.type === "text"),
    hasImages: currentElements.some(el => el.type === "image"),
  };

  // Generate content via real API
  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Veuillez entrer une description");
      return;
    }
    
    setLoading(true);
    setResult(null);
    setError(null);
    
    try {
      let apiResult;
      
      if (contentType === "text") {
        // Map subject to text_type
        const textTypeMap = {
          lecture: "histoire",
          grammaire: "documentaire",
          vocabulaire: "documentaire",
          maths: "documentaire",
          sciences: "documentaire",
          histoire: "documentaire",
        };
        
        const response = await aiGenerateText({
          theme: prompt,
          niveau: niveau,
          text_type: textTypeMap[subject] || "histoire",
          word_count: 100,
          include_dialogue: false,
        });
        
        apiResult = {
          title: response.data.title || `Texte - ${prompt}`,
          content: response.data.text || response.data.raw_content || "",
          niveau: response.data.niveau,
          type: "text",
          rawData: response.data,
          suggestions: [
            "Ajouter une illustration",
            "Créer un exercice associé",
            "Adapter pour un autre niveau",
          ],
        };
      } else {
        // Exercise generation
        const exerciseTypeMap = {
          lecture: "trous",
          grammaire: "qcm",
          vocabulaire: "association",
          maths: "qcm",
          sciences: "vrai_faux",
          histoire: "qcm",
        };
        
        const response = await aiGenerateExercise({
          topic: prompt,
          niveau: niveau,
          exercise_type: exerciseTypeMap[subject] || "qcm",
          question_count: 5,
          subject: subject,
        });
        
        const data = response.data;
        let formattedContent = "";
        
        if (data.title) formattedContent += `${data.title}\n\n`;
        if (data.instructions) formattedContent += `Consigne : ${data.instructions}\n\n`;
        
        if (data.questions && data.questions.length > 0) {
          data.questions.forEach((q, i) => {
            formattedContent += `${i + 1}. ${q.question}\n`;
            if (q.options) {
              q.options.forEach((opt, j) => {
                const letters = ['a', 'b', 'c', 'd'];
                formattedContent += `   ${letters[j]}) ${opt}\n`;
              });
            }
            formattedContent += "\n";
          });
        } else if (data.raw_content) {
          formattedContent = data.raw_content;
        }
        
        apiResult = {
          title: data.title || `Exercice - ${prompt}`,
          content: formattedContent.trim(),
          niveau: data.niveau,
          type: "exercise",
          rawData: data,
          questions: data.questions,
          suggestions: [
            "Ajouter plus de questions",
            "Simplifier pour un niveau inférieur",
            "Ajouter des indices",
          ],
        };
      }
      
      setResult(apiResult);
      toast.success("Contenu généré avec succès !");
    } catch (err) {
      console.error("AI generation error:", err);
      const errorMsg = err.response?.data?.detail || "Erreur lors de la génération. Vérifiez votre connexion.";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Handle structure selection
  const handleSelectStructure = (structure) => {
    setSelectedStructure(structure);
    onGenerateStructure?.(structure);
    toast.success(`Structure "${structure.name}" ajoutée`);
  };

  // Handle chat with real API
  const handleSendChat = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = { role: "user", content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    const currentInput = chatInput;
    setChatInput("");
    setLoading(true);
    
    try {
      // Build context from current document
      let contextStr = null;
      if (documentContext.elementCount > 0) {
        contextStr = `Document: "${documentContext.title}", ${documentContext.elementCount} éléments, ` +
          `contient du texte: ${documentContext.hasText}, contient des images: ${documentContext.hasImages}`;
      }
      
      // Convert messages to API format
      const historyForApi = chatMessages.slice(-8).map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      
      const response = await aiChat(currentInput, contextStr, historyForApi);
      
      const assistantResponse = response.data?.response || "Je n'ai pas pu traiter votre demande.";
      setChatMessages(prev => [...prev, { role: "assistant", content: assistantResponse }]);
      
    } catch (err) {
      console.error("Chat error:", err);
      const errorMsg = err.response?.data?.detail || "Erreur de communication avec l'assistant.";
      setChatMessages(prev => [...prev, { 
        role: "assistant", 
        content: `⚠️ ${errorMsg}` 
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Copy result
  const handleCopy = () => {
    if (result?.content) {
      navigator.clipboard.writeText(result.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Copié !");
    }
  };

  // Insert result as formatted text element for Konva canvas
  const handleInsert = () => {
    if (result) {
      // Format result for ContentEditorPage
      // It will receive this and add as text element to the canvas
      const formattedResult = {
        title: result.title,
        text: result.content,
        niveau: result.niveau,
        type: result.type,
        questions: result.questions || null,
        rawData: result.rawData || null,
      };
      onGenerateText?.(formattedResult);
      toast.success("Contenu ajouté à la fiche");
      onClose?.();
    }
  };

  // Regenerate with same parameters
  const handleRegenerate = () => {
    setResult(null);
    setError(null);
    handleGenerate();
  };

  // Scroll to result when it appears
  const scrollAreaRef = useRef(null);
  
  useEffect(() => {
    if (result && scrollAreaRef.current) {
      // Scroll to bottom to show result
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        setTimeout(() => {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }, 100);
      }
    }
  }, [result]);

  return (
    <div className="flex flex-col" style={{ minHeight: '450px' }}>
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-slate-200 flex-shrink-0">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="font-bold text-slate-800">Assistant Navi</h2>
          <p className="text-xs text-slate-500">IA pédagogique contextuelle</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col mt-4">
        <TabsList className="grid grid-cols-4 mb-4 flex-shrink-0">
          <TabsTrigger value="generate" className="text-xs">
            <Wand2 className="w-4 h-4 mr-1" />
            Générer
          </TabsTrigger>
          <TabsTrigger value="structures" className="text-xs">
            <Layers className="w-4 h-4 mr-1" />
            Structures
          </TabsTrigger>
          <TabsTrigger value="differentiate" className="text-xs">
            <Users className="w-4 h-4 mr-1" />
            Adapter
          </TabsTrigger>
          <TabsTrigger value="chat" className="text-xs">
            <MessageSquare className="w-4 h-4 mr-1" />
            Chat
          </TabsTrigger>
        </TabsList>

        {/* Generate Tab */}
        <TabsContent value="generate" className="flex-1">
          <div ref={scrollAreaRef} className="space-y-4">
            {/* Show result if exists, otherwise show form */}
            {result ? (
              /* Result View */
              <div className="animate-in fade-in duration-300">
                <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-300 shadow-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-500" />
                      <h3 className="font-bold text-purple-800">{result.title}</h3>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={handleCopy}
                        className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                        title="Copier"
                        data-testid="ai-copy-btn"
                      >
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-purple-500" />}
                      </button>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-sm text-slate-700 whitespace-pre-wrap max-h-48 overflow-y-auto border border-purple-100" data-testid="ai-result-content">
                    {result.content}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={handleInsert} className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600" data-testid="ai-insert-btn">
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Insérer dans la fiche
                    </Button>
                    <Button variant="outline" onClick={() => { setResult(null); setError(null); }} data-testid="ai-new-btn">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Nouveau
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              /* Form View */
              <>
                {/* Content type */}
                <div className="flex gap-2">
              <button
                onClick={() => setContentType("text")}
                className={`flex-1 py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-colors ${
                  contentType === "text" 
                    ? "bg-purple-100 text-purple-700 ring-2 ring-purple-300" 
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <BookOpen size={18} />
                Texte
              </button>
              <button
                onClick={() => setContentType("exercise")}
                className={`flex-1 py-3 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-colors ${
                  contentType === "exercise" 
                    ? "bg-purple-100 text-purple-700 ring-2 ring-purple-300" 
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <PenTool size={18} />
                Exercice
              </button>
            </div>

            {/* Level and subject */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <Label className="text-xs mb-1.5 block">Niveau</Label>
                <Select value={niveau} onValueChange={setNiveau}>
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NIVEAUX.map((n) => (
                      <SelectItem key={n.id} value={n.id}>
                        <span className="flex items-center gap-2">
                          <GraduationCap className="w-4 h-4" />
                          {n.shortName} ({n.age})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1.5 block">Matière</Label>
                <Select value={subject} onValueChange={setSubject}>
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lecture">📖 Lecture</SelectItem>
                    <SelectItem value="grammaire">✏️ Grammaire</SelectItem>
                    <SelectItem value="vocabulaire">📚 Vocabulaire</SelectItem>
                    <SelectItem value="maths">🧮 Maths</SelectItem>
                    <SelectItem value="sciences">🔬 Sciences</SelectItem>
                    <SelectItem value="histoire">🏛️ Histoire</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Prompt input */}
            <div className="mb-4">
              <Label className="text-xs mb-1.5 block">Décrivez ce que vous souhaitez créer</Label>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Ex: Un texte sur les animaux de la ferme avec des questions de compréhension..."
                className="min-h-[80px] resize-none"
              />
            </div>

            {/* Quick prompts */}
            <div className="mb-4">
              <Label className="text-xs mb-2 block text-slate-500">Suggestions rapides</Label>
              <div className="flex flex-wrap gap-2">
                {QUICK_PROMPTS[subject]?.slice(0, 4).map((quickPrompt, i) => (
                  <button
                    key={i}
                    onClick={() => setPrompt(quickPrompt)}
                    className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 rounded-full text-slate-600 transition-colors"
                  >
                    {quickPrompt}
                  </button>
                ))}
              </div>
            </div>

            {/* Generate button */}
            <Button 
              onClick={handleGenerate} 
              disabled={loading || !prompt.trim()}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              data-testid="ai-generate-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Génération en cours...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Générer le contenu
                </>
              )}
            </Button>

            {/* Error display */}
            {error && (
              <div className="mt-4 p-4 bg-red-50 rounded-xl border border-red-200 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-red-700 font-medium">Erreur de génération</p>
                  <p className="text-xs text-red-600 mt-1">{error}</p>
                </div>
              </div>
            )}
              </>
            )}
          </div>
        </TabsContent>

        {/* Structures Tab */}
        <TabsContent value="structures" className="flex-1 overflow-hidden">
          <ScrollArea className="h-full pr-4">
            <p className="text-sm text-slate-600 mb-4">
              Choisissez une structure pédagogique prête à l'emploi
            </p>
            <div className="grid grid-cols-2 gap-3">
              {STRUCTURE_TEMPLATES.map((structure) => (
                <button
                  key={structure.id}
                  onClick={() => handleSelectStructure(structure)}
                  className="p-4 rounded-xl border-2 border-slate-200 hover:border-purple-400 transition-all text-left hover:shadow-md"
                >
                  <span className="text-2xl mb-2 block">{structure.icon}</span>
                  <p className="font-medium text-slate-800">{structure.name}</p>
                  <p className="text-xs text-slate-500 mt-1">{structure.description}</p>
                </button>
              ))}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* Differentiate Tab */}
        <TabsContent value="differentiate" className="flex-1 overflow-hidden">
          <ScrollArea className="h-full pr-4">
            <p className="text-sm text-slate-600 mb-4">
              Adaptez votre contenu pour différents besoins
            </p>
            
            {documentContext.elementCount > 0 ? (
              <>
                <div className="p-3 bg-slate-50 rounded-xl mb-4">
                  <p className="text-xs text-slate-500 mb-1">Contenu actuel</p>
                  <p className="text-sm font-medium text-slate-700">
                    {documentContext.title || "Sans titre"} ({documentContext.elementCount} éléments)
                  </p>
                </div>
                
                <div className="space-y-2">
                  {DIFFERENTIATION_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => {
                        toast.info(`Adaptation "${option.label}" - Fonctionnalité en cours de développement`);
                      }}
                      className="w-full p-3 rounded-xl border-2 border-slate-200 hover:border-purple-400 transition-all text-left flex items-center gap-3"
                    >
                      <span className="text-xl">{option.icon}</span>
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{option.label}</p>
                        <p className="text-xs text-slate-500">{option.description}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">
                  Ajoutez du contenu à votre fiche pour utiliser les options de différenciation
                </p>
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Chat Tab */}
        <TabsContent value="chat" className="flex-1 flex flex-col overflow-hidden">
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-4">
              {chatMessages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] p-3 rounded-2xl ${
                      msg.role === "user"
                        ? "bg-purple-500 text-white rounded-br-md"
                        : "bg-slate-100 text-slate-800 rounded-bl-md"
                    }`}
                  >
                    <p className="text-sm">{msg.content}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 p-3 rounded-2xl rounded-bl-md">
                    <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
          
          <div className="flex gap-2 mt-4 pt-4 border-t border-slate-200">
            <Input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSendChat()}
              placeholder="Posez une question à Navi..."
              className="flex-1"
            />
            <Button onClick={handleSendChat} disabled={loading || !chatInput.trim()}>
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default AIAssistantPanel;
